import Bitmap from "../bitmap";
import { getBlockPackForCanvas } from "../packs/blocks/BlockPackFactory";
import { getCanvasPackForCanvas } from "../packs/canvas/CanvasPackFactory";
import Color from "../models/color";
import Block from "../models/block";
import { ICanvas } from "../types/canvas";
import { IGame } from "../types/game";

export interface ICanvasView {
  draw(): HTMLCanvasElement;
}

export default class CanvasView {
  public height: number;
  public width: number;
  public screen: HTMLCanvasElement;
  private context: CanvasRenderingContext2D;
  private bitmap: Bitmap;
  private game: IGame;
  private canvas: ICanvas;
  blockSize: number;

  constructor(screen: HTMLCanvasElement, game: IGame, width: number) {
    this.screen = screen;
    this.context = screen.getContext("2d");
    if (this.context) {
      // Disable image smoothing for crisp pixel rendering
      this.context.imageSmoothingEnabled = false;
      this.context.imageSmoothingQuality = "high";
    }
    this.bitmap = new Bitmap(this.context);
    this.game = game;
    this.canvas = game.canvas;
    // Calculate blockSize accounting for borders (1px on each side)
    // Blocks need to fit within width - 2 (1px left + 1px right border)
    // So: 10 * blockSize = width - 2, therefore blockSize = (width - 2) / 10
    this.blockSize = (width - 2) / 10;
    this.width = this.screen.width = width;
    // Calculate height based on 20 rows of blocks + borders (1px top + 1px bottom)
    // Height = 20 * blockSize + 2 = 20 * ((width - 2) / 10) + 2 = 2 * (width - 2) + 2 = 2 * width - 2
    this.height = this.screen.height = 20 * this.blockSize + 2;
  }

  draw(frameTime: number): HTMLCanvasElement {
    let block = this.canvas.bloc;
    this.clear();
    this.drawBackground(frameTime);
    this.drawFrame(frameTime);
    this.drawBlocks();

    // Render current piece (always visible, even during countdown - matches original)
    if (block) {
      if (this.canvas.blocShadow) this.drawBlock(this.canvas.blocShadow);
      if (this.canvas.smooth) this.drawBlock(block);
      else this.drawBlock(block, (block.x - 4) * 18, (block.y - 12) * 18);
    }
    
    // Draw overlay effects from pack (on top of blocks)
    this.drawOverlay(frameTime);

    return this.screen;
  }

  private clear() {
    this.context.clearRect(0, 0, this.width, this.height);
  }

  private drawBlock(block: Block, x: number = -1, y: number = -1) {
    // Border width offset to prevent blocks from overlapping the border
    const borderOffset = 1;
    if (x === -1) x = block.posX >> 4;
    if (y === -1) y = block.posY >> 4;
    const pack = getBlockPackForCanvas(this.canvas);
    for (let brick of block.getBlueprint()) {
      // Use integer math - blockSize is already an integer, offset by border width
      const brickX = borderOffset + (x / 18) * this.blockSize + brick.x * this.blockSize;
      const brickY = borderOffset + (y / 18) * this.blockSize + brick.y * this.blockSize;
      pack.drawBlock(
        this.bitmap,
        brickX,
        brickY,
        brick.t,
        this.blockSize,
        new Color(brick.color, this.game.palette),
        brick.to
      );
    }
  }

  // draw
  private drawBlocks(): void {
    // Border width offset to prevent blocks from overlapping the border
    const borderOffset = 1;
    for (let row = 12; row < 32; row++) {
      for (let col = 4; col < 14; col++) {
        if (this.canvas.occupied[row][col]) {
          let side: number,
            color: number,
            to: number[] = new Array(4),
            w: number = this.blockSize;

          side = this.canvas.blocks[row][col] & 15;
          color = this.canvas.blocks[row][col] >> 4;
          // Get neighbor blocks (use 0 if out of bounds)
          to[0] =
            col > 4 && this.canvas.blocks[row]
              ? this.canvas.blocks[row][col - 1] || 0
              : 0;
          to[1] =
            row > 0 && this.canvas.blocks[row - 1]
              ? this.canvas.blocks[row - 1][col] || 0
              : 0;
          to[2] =
            col < 13 && this.canvas.blocks[row]
              ? this.canvas.blocks[row][col + 1] || 0
              : 0;
          to[3] =
            row < 31 && this.canvas.blocks[row + 1]
              ? this.canvas.blocks[row + 1][col] || 0
              : 0;

          // Use integer math to ensure perfect alignment, offset by border width
          const x = borderOffset + (col - 4) * w;
          const y = borderOffset + (row - 12) * w;
          const pack = getBlockPackForCanvas(this.canvas);
          pack.drawBlock(
            this.bitmap,
            x,
            y,
            side,
            w,
            new Color(color, this.game.palette),
            to
          );
        }
      }
    }
  }

  private drawBackground(frameTime: number): void {
    const pack = getCanvasPackForCanvas(this.canvas);
    const level = this.canvas?.level;
    pack.drawBackground(this.context, this.width, this.height, frameTime, level);
  }

  private drawFrame(frameTime: number): void {
    const pack = getCanvasPackForCanvas(this.canvas);
    pack.drawFrame(this.context, this.width, this.height, frameTime);
  }

  // draw
  private drawOverlay(frameTime: number): void {
    const pack = getCanvasPackForCanvas(this.canvas);
    pack.drawOverlay(this.context, this.width, this.height, frameTime);
  }
}
